import axios from "axios";
import React, { useEffect, useState } from "react";
import {
  Typography,
  Container,
  Box,
  Button,
  Link,
  Breadcrumbs,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Tooltip,
  Paper,
  Chip,
  Avatar,
  Pagination,
  Tabs,
  Tab,
} from "@mui/material";
import { useNavigate, useParams } from "react-router-dom";
import {
  Add as AddIcon,
  Edit as EditIcon,
  DirectionsCar as DriverIcon,
  CleaningServices as CleanerIcon,
  Group,
  SupervisorAccount,
  CalendarMonth,
} from "@mui/icons-material";
import { Loading } from "../../components/Loading";
import { getValidToken } from "../../utils/authUtils";
import NavigationHeader from "../../components/NavigationHeader";
import {
  commonTableContainerProps,
  tableHeaderStyles,
  tableCellStyles,
  smallButtonProp,
} from "../../utils/styles";
import { usePlanStatus } from "../../hooks/usePlanStatus";
import { formatCurrency, formatDate, formatText } from "../../utils/utils";

interface Staff {
  id: string;
  companyId: string;
  name: string;
  contactNumber: string;
  alternateNumber: string;
  address: string;
  status: "ACTIVE" | "INACTIVE" | "BLACKLISTED";
  type: "DRIVER" | "CLEANER" | "LADY" | "SUPERVISOR" | "OTHER";
  employmentType: "PERMANENT" | "TEMPORARY";
  joiningDate: string;
  salary: string;
  aadharNumber: string;
  drivingLicense?: string;
  referredByName: string;
  referredByContact: string;
  reference1Name: string;
  reference1Contact: string;
  reference2Name: string;
  reference2Contact: string;
  photoUrl: string | null;
  notes: string;
  createdAt: string;
  updatedAt: string;
}

interface TabPanelProps {
  children?: React.ReactNode;
  value: string;
  index: string;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`staff-tabpanel-${index}`}
      aria-labelledby={`staff-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ py: 3 }}>{children}</Box>}
    </div>
  );
}

const DriverCleaner = () => {
  const [staffMembers, setStaffMembers] = useState<Staff[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const { type } = useParams();
  const [currentTab, setCurrentTab] = useState<
    "DRIVER" | "CLEANER" | "LADY" | "SUPERVISOR" | "OTHER"
  >(type as "DRIVER" | "CLEANER" | "LADY" | "SUPERVISOR" | "OTHER");
  const navigate = useNavigate();

  useEffect(() => {
    fetchStaffMembers();
  }, [page, currentTab]);

  const fetchStaffMembers = async () => {
    setIsLoading(true);
    try {
      const queryParams = new URLSearchParams({
        page: page.toString(),
        limit: "10",
        type: currentTab,
      });
      const url = `${process.env.REACT_APP_API_HOST}staff-members?${queryParams}`;
      const token = await getValidToken();
      const response = await axios.get(url, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      if (response.data?.isSuccess) {
        setStaffMembers(response.data?.data);
        setTotalPages(response.data?.pagination?.pages || 1);
      } else {
        throw new Error("Failed to fetch staff members");
      }
    } catch (error) {
      return [];
    } finally {
      setIsLoading(false);
    }
  };

  const handleStaffClick = (type: string, staffId: string) => {
    navigate(`/master-data/staff/${type}/${staffId}`);
  };

  const handleTabChange = (
    event: React.SyntheticEvent,
    newValue: "DRIVER" | "CLEANER" | "LADY" | "SUPERVISOR" | "OTHER"
  ) => {
    setCurrentTab(newValue);
    navigate(`/master-data/staff/${newValue}`);
    setPage(1); // Reset page when changing tabs
  };

  const showPhoto = (photoUrl: string | null) => {
    if (photoUrl) {
      window.open(photoUrl, "_blank");
    } else {
      return;
    }
  };

  const RenderTabContent = ({
    type,
    canEdit = false,
  }: {
    type: "Driver" | "Cleaner" | "Lady" | "Supervisor";
    canEdit?: boolean;
  }) => (
    <>
      <TableContainer sx={commonTableContainerProps} component={Paper}>
        <Table
          sx={tableHeaderStyles}
          aria-label="work force table"
          size="small"
        >
          <TableHead>
            <TableRow>
              <TableCell sx={tableHeaderStyles}>Photo</TableCell>
              <TableCell sx={tableHeaderStyles}>Name</TableCell>
              <TableCell sx={tableHeaderStyles}>Status</TableCell>
              <TableCell sx={tableHeaderStyles}>Contact Details</TableCell>
              <TableCell sx={tableHeaderStyles}>Employment</TableCell>
              <TableCell sx={tableHeaderStyles}>Joining Date</TableCell>
              <TableCell sx={tableHeaderStyles}>Salary</TableCell>
              <TableCell sx={tableHeaderStyles}>Documents</TableCell>
              {type !== "Lady" && type !== "Supervisor" && (
                <TableCell sx={tableHeaderStyles}>References</TableCell>
              )}
              {canEdit && (
                <TableCell align="center" sx={tableHeaderStyles}>
                  Actions
                </TableCell>
              )}
            </TableRow>
          </TableHead>
          <TableBody>
            {staffMembers.length === 0 && (
              <TableRow>
                <TableCell
                  colSpan={10}
                  sx={{ ...tableCellStyles, textAlign: "center" }}
                >
                  No {type} found
                </TableCell>
              </TableRow>
            )}
            {staffMembers.map((staff) => (
              <TableRow
                key={staff.id}
                sx={{
                  "&:last-child td, &:last-child th": { border: 0 },
                  "&:hover": { backgroundColor: "action.hover" },
                }}
              >
                <TableCell sx={tableCellStyles}>
                  <Avatar
                    src={staff?.photoUrl || ""}
                    sx={{
                      width: 32,
                      height: 32,
                      borderRadius: "50%",
                      "&:hover": {
                        cursor: "pointer",
                        border: "1px solid #000",
                      },
                    }}
                    onClick={() => showPhoto(staff?.photoUrl || "")}
                  />
                </TableCell>
                <TableCell component="th" scope="row" sx={tableCellStyles}>
                  {staff.name}
                </TableCell>
                <TableCell sx={tableCellStyles}>
                  <Tooltip title={staff.notes}>
                    <Chip
                      label={staff.status}
                      color={
                        staff.status === "ACTIVE"
                          ? "success"
                          : staff.status === "BLACKLISTED"
                            ? "error"
                            : "default"
                      }
                      size="small"
                      sx={{
                        fontSize: "0.75rem",
                        lineHeight: "1.3",
                      }}
                    />
                  </Tooltip>
                </TableCell>
                <TableCell sx={tableCellStyles}>
                  {staff.contactNumber ? (
                    <Typography variant="caption" sx={{ fontSize: "0.75rem" }}>
                      {staff.contactNumber}
                      {staff.alternateNumber &&
                        `, ${staff.alternateNumber && "Alt: "}`}
                      {staff.alternateNumber}
                    </Typography>
                  ) : (
                    "-"
                  )}
                </TableCell>
                <TableCell sx={tableCellStyles}>
                  {formatText(staff.employmentType)}
                </TableCell>
                <TableCell sx={tableCellStyles}>
                  {staff.joiningDate && staff.employmentType === "PERMANENT"
                    ? formatDate(staff.joiningDate)
                    : "-"}
                </TableCell>
                <TableCell sx={tableCellStyles}>
                  {staff.employmentType === "PERMANENT"
                    ? formatCurrency(staff.salary)
                    : "-"}
                </TableCell>
                <TableCell>
                  <Box sx={{ display: "flex", gap: 0.5, flexWrap: "wrap" }}>
                    {staff.aadharNumber && <Chip label="Aadhar" size="small" />}
                    {staff.drivingLicense && staff.type === "DRIVER" && (
                      <Chip label="License" size="small" />
                    )}
                    {!staff.aadharNumber && !staff.drivingLicense && "-"}
                  </Box>
                </TableCell>
                {type !== "Lady" && type !== "Supervisor" && (
                  <TableCell sx={tableCellStyles}>
                    <Tooltip
                      title={`Referred by: ${staff.referredByName} (${staff.referredByContact})`}
                    >
                      <Typography
                        variant="caption"
                        sx={{
                          maxWidth: 150,
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap",
                          cursor: "pointer",
                        }}
                      >
                        {staff.referredByName || "-"}
                      </Typography>
                    </Tooltip>
                  </TableCell>
                )}
                <TableCell align="center" sx={tableCellStyles}>
                  <Tooltip title="Edit">
                    <IconButton
                      size="small"
                      onClick={() => handleStaffClick(type, staff.id)}
                    >
                      <EditIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      {totalPages > 1 && (
        <Box display="flex" justifyContent="center" mt={4}>
          <Pagination
            count={totalPages}
            page={page}
            onChange={(_, value) => setPage(value)}
            color="primary"
          />
        </Box>
      )}
    </>
  );

  const { planStatus } = usePlanStatus();

  if (isLoading) {
    return (
      <Loading message={`Loading ${currentTab}'s...`} fullScreen={false} />
    );
  }
  // 'CLUSTER_LEADER','AREA_MANAGER','OPERATIONS_HEAD','OPERATIONS_MANAGER','MAINTENANCE_ENGINEER','MAINTENANCE_SUPERVISOR','MAINTENANCE_MANAGER','PILOT'

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      <NavigationHeader
        breadcrumbs={[
          { label: "Master Data", path: "/master-data" },
          { label: "Work Force" },
        ]}
        title={type}
        buttons={[
          {
            label: `Add ${type === "Lady" ? "Lady Attendant" : type}`,
            onClick: () => navigate(`/master-data/staff/${type}/new`),
      icon: <AddIcon />,
          },
        ]}
      backNavigationTo={"/master-data"}
      />
      <Box sx={{ borderBottom: 1, borderColor: "divider" }}>
        <Tabs
          value={currentTab}
          onChange={handleTabChange}
          sx={{
            minHeight: "36px",
            "& .MuiTab-root": {
              fontSize: "0.75rem",
              fontWeight: "500",
              minHeight: "36px",
              padding: "6px 12px",
            },
          }}
        >
          {planStatus?.permissions?.canViewDriver && (
            <Tab
              icon={<DriverIcon fontSize="small" />}
              label="Cluster Leaders"
              value="CLUSTER_LEADER"
              sx={{
                textTransform: "none",
              }}
            />
          )}
          {planStatus?.permissions?.canViewCleaner && (
            <Tab
              icon={<CleanerIcon fontSize="small" />}
              label="Area Managers"
              value="Area"
              sx={{
                textTransform: "none",
              }}
            />
          )}
          {planStatus?.permissions?.canViewLadyAttendant && (
            <Tab
              icon={<Group fontSize="small" />}
              label="Staff"
              value="STAFF"
              sx={{
                textTransform: "none",
              }}
            />
          )}
          {planStatus?.permissions?.canViewSupervisor && (
            <Tab
              icon={<SupervisorAccount fontSize="small" />}
              label="Supervisor"
              value="SUPERVISOR"
              sx={{
                textTransform: "none",
              }}
            />
          )}
        </Tabs>
      </Box>
      <TabPanel value={currentTab} index="DRIVER">
        <RenderTabContent
          type="Driver"
          canEdit={planStatus?.permissions?.canEditDriver}
        />
      </TabPanel>

      <TabPanel value={currentTab} index="CLEANER">
        <RenderTabContent
          type="Cleaner"
          canEdit={planStatus?.permissions?.canEditCleaner}
        />
      </TabPanel>

      <TabPanel value={currentTab} index="LADY">
        <RenderTabContent
          type="Lady"
          canEdit={planStatus?.permissions?.canEditLadyAttendant}
        />
      </TabPanel>

      <TabPanel value={currentTab} index="SUPERVISOR">
        <RenderTabContent
          type="Supervisor"
          canEdit={planStatus?.permissions?.canEditSupervisor}
        />
      </TabPanel>
    </Container>
  );
};

export default DriverCleaner;
